/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/AppointmentScheduleModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import CustomCalendar from "../../components/CustomCalendar";
import styles from "./AppointmentScheduleModal.module.css";
import { getUserSentAppointmentsForEvent } from "../../logic/appointment";

import { ReactComponent as CloseIcon } from "../../assets/icons/AppointmentScheduleModal/CloseIcon.svg";
import { ReactComponent as AutoAssignIcon } from "../../assets/icons/AppointmentScheduleModal/AutoAssignIcon.svg";

const AppointmentScheduleModal = ({
  open,
  onClose,
  onConfirm,
  appointmentData = null,
  eventData = null,
  participatingCompanyData = null, // Participating company data with tiempoBreak
  appointments = [],
  user = null,
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState(null);
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedAppointmentTime, setSelectedAppointmentTime] = useState(null);
  const [timeOptions, setTimeOptions] = useState([]);
  const [senderAppointments, setSenderAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);

  // Parse break time from participating company's tiempoBreak (format: "13:00-13:30")
  const getBreakTimes = () => {
    if (!participatingCompanyData || !participatingCompanyData.tiempoBreak) {
      return null;
    }

    try {
      // Parse tiempoBreak format: "13:00-13:30"
      const [startTime, endTime] =
        participatingCompanyData.tiempoBreak.split("-");
      if (!startTime || !endTime) {
        return null;
      }

      return {
        horaInicioBreak: startTime.trim(),
        horaFinBreak: endTime.trim(),
      };
    } catch (error) {
      logger.error("Error parsing tiempoBreak:", { err: sanitizeError(error) });
      return null;
    }
  };

  // Helper function to check if a time slot overlaps with the break time
  const isTimeSlotInBreak = (hour, minute, durationMinutes) => {
    const breakTimes = getBreakTimes();
    if (
      !breakTimes ||
      !breakTimes.horaInicioBreak ||
      !breakTimes.horaFinBreak
    ) {
      return false;
    }

    // Parse break start and end times
    const [breakStartHour, breakStartMinute] = breakTimes.horaInicioBreak
      .split(":")
      .map(Number);
    const [breakEndHour, breakEndMinute] = breakTimes.horaFinBreak
      .split(":")
      .map(Number);

    // Convert to minutes for easier comparison
    const breakStartMinutes = breakStartHour * 60 + breakStartMinute;
    const breakEndMinutes = breakEndHour * 60 + breakEndMinute;
    const slotStartMinutes = hour * 60 + minute;
    const slotEndMinutes = slotStartMinutes + durationMinutes;

    // Check if the time slot overlaps with the break
    return (
      (slotStartMinutes >= breakStartMinutes &&
        slotStartMinutes < breakEndMinutes) ||
      (slotEndMinutes > breakStartMinutes &&
        slotEndMinutes <= breakEndMinutes) ||
      (slotStartMinutes <= breakStartMinutes &&
        slotEndMinutes >= breakEndMinutes)
    );
  };

  // Helper function to get available time slots for a specific date
  const getAvailableTimeSlotsForDate = (date) => {
    if (!eventData || !eventData.horaInicio || !eventData.horaFin) {
      return [];
    }

    // Parse event start time (format: "13:30")
    const startTimeParts = eventData.horaInicio.split(":");
    const startHour = parseInt(startTimeParts[0], 10);
    const startMinute = parseInt(startTimeParts[1], 10);

    // Parse event end time (format: "20:00")
    const endTimeParts = eventData.horaFin.split(":");
    const endHour = parseInt(endTimeParts[0], 10);
    const endMinute = parseInt(endTimeParts[1], 10);

    const availableSlots = [];
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    const todayHour = today.getHours();
    const todayMinute = today.getMinutes();

    // Get appointment duration from event data
    let durationMinutes = 60; // Default to 60 minutes (1 hour)

    if (eventData.duracionCitas) {
      // Parse event duration (format: "00:20", "01:30", etc.)
      const [hours, minutes] = eventData.duracionCitas.split(":").map(Number);
      durationMinutes = hours * 60 + minutes;
    }

    // Get break times from participating company data if they exist
    let breakStartMinutes = null;
    let breakEndMinutes = null;
    const breakTimes = getBreakTimes();
    if (breakTimes && breakTimes.horaInicioBreak && breakTimes.horaFinBreak) {
      const [breakStartHour, breakStartMinute] = breakTimes.horaInicioBreak
        .split(":")
        .map(Number);
      const [breakEndHour, breakEndMinute] = breakTimes.horaFinBreak
        .split(":")
        .map(Number);
      breakStartMinutes = breakStartHour * 60 + breakStartMinute;
      breakEndMinutes = breakEndHour * 60 + breakEndMinute;
    }

    // Start from the exact event start time
    let currentHour = startHour;
    let currentMinute = startMinute;

    // Continue until we reach or exceed the event end time
    while (
      currentHour < endHour ||
      (currentHour === endHour && currentMinute < endMinute)
    ) {
      const currentMinutes = currentHour * 60 + currentMinute;
      const slotEndMinutes = currentMinutes + durationMinutes;

      // Check if this time slot overlaps with the break - if so, skip to after the break
      if (breakStartMinutes !== null && breakEndMinutes !== null) {
        // A slot overlaps with the break if:
        // 1. It starts during the break (between breakStart and breakEnd, exclusive of breakEnd)
        // 2. It ends during the break (slotEnd is between breakStart and breakEnd, exclusive of breakStart)
        // 3. It spans the entire break (starts before and ends after)
        // Note: If a slot ends exactly when break starts (slotEnd == breakStart), it's valid
        // Note: If a slot starts exactly when break ends (currentMinutes == breakEnd), it's valid
        const overlapsBreak =
          (currentMinutes >= breakStartMinutes &&
            currentMinutes < breakEndMinutes) ||
          (slotEndMinutes > breakStartMinutes &&
            slotEndMinutes <= breakEndMinutes) ||
          (currentMinutes < breakStartMinutes &&
            slotEndMinutes > breakEndMinutes);

        if (overlapsBreak) {
          // Skip to the break end time and continue from there
          currentHour = Math.floor(breakEndMinutes / 60);
          currentMinute = breakEndMinutes % 60;
          continue;
        }
      }

      // Check if this time slot has enough time (doesn't exceed business event end time)
      // Note: slotEndMinutes was already calculated above for break checking
      const eventEndMinutes = endHour * 60 + endMinute;
      const hasEnoughTime = slotEndMinutes <= eventEndMinutes;

      // Skip past time slots if it's today
      if (isToday) {
        if (
          currentHour < todayHour ||
          (currentHour === todayHour && currentMinute <= todayMinute)
        ) {
          // Move to next time slot
          currentMinute += durationMinutes;
          if (currentMinute >= 60) {
            currentHour += Math.floor(currentMinute / 60);
            currentMinute = currentMinute % 60;
          }
          continue;
        }
      }

      // Only add slots that have enough time
      if (hasEnoughTime) {
        const timeString = `${currentHour
          .toString()
          .padStart(2, "0")}:${currentMinute.toString().padStart(2, "0")}`;
        const isAvailable = isTimeSlotAvailable(
          date,
          currentHour,
          currentMinute,
        );

        // Check for company user conflicts if we have appointment data
        const hasConflict =
          appointmentData && appointmentData.idEmpresaUsuarioRemitente
            ? hasCompanyUserConflict(
                date,
                currentHour,
                currentMinute,
                durationMinutes,
                appointmentData.idEmpresaUsuarioRemitente,
              )
            : false;

        if (isAvailable && !hasConflict) {
          availableSlots.push({
            value: timeString,
            label: timeString,
            hour: currentHour,
            minute: currentMinute,
          });
        }
      }

      // Move to next time slot
      currentMinute += durationMinutes;
      if (currentMinute >= 60) {
        currentHour += Math.floor(currentMinute / 60);
        currentMinute = currentMinute % 60;
      }
    }

    return availableSlots;
  };

  // Helper function to check if a time slot is in the past
  const isTimeSlotInPast = (date, hour, minute = 0) => {
    const now = new Date();
    const slotDateTime = new Date(date);
    slotDateTime.setHours(hour, minute, 0, 0);

    return slotDateTime < now;
  };

  // Helper function to check for company user conflicts
  const hasCompanyUserConflict = (
    date,
    hour,
    minute,
    durationMinutes,
    companyUserId,
  ) => {
    if (!companyUserId) {
      return false;
    }

    // Get all appointments for this date
    const dayAppointments = allAppointments.filter((appointment) => {
      if (!appointment.horaInicio || appointment.estado !== "approved") {
        return false;
      }
      const appointmentDate = new Date(appointment.fecha + "T00:00:00");
      return appointmentDate.toDateString() === date.toDateString();
    });

    // Check if any appointment on this date has the same idEmpresaUsuarioRemitente
    // Exclude the current appointment being rescheduled (if any)
    const sameCompanyAppointments = dayAppointments.filter(
      (appointment) =>
        appointment.idEmpresaUsuarioRemitente === companyUserId &&
        appointment._id !== appointmentData?._id,
    );

    if (sameCompanyAppointments.length === 0) {
      return false;
    }

    // Calculate the proposed appointment time
    const proposedStartMinutes = hour * 60 + minute;
    const proposedEndMinutes = proposedStartMinutes + durationMinutes;

    // Create a list of all appointments from the same company, including the proposed one
    const allAppointmentsList = sameCompanyAppointments.map((appointment) => {
      const [startHour, startMinute] = appointment.horaInicio
        .split(":")
        .map(Number);
      const [endHour, endMinute] = appointment.horaFin.split(":").map(Number);
      return {
        start: startHour * 60 + startMinute,
        end: endHour * 60 + endMinute,
        id: appointment._id,
        isProposed: false,
      };
    });

    // Add the proposed appointment
    allAppointmentsList.push({
      start: proposedStartMinutes,
      end: proposedEndMinutes,
      id: "proposed",
      isProposed: true,
    });

    // Sort by start time
    allAppointmentsList.sort((a, b) => a.start - b.start);

    // Check if this would create a block of 2 or more consecutive appointments
    for (let i = 0; i < allAppointmentsList.length - 1; i++) {
      const current = allAppointmentsList[i];
      const next = allAppointmentsList[i + 1];

      // If appointments are consecutive (current ends when next starts)
      if (current.end === next.start) {
        // Count how many consecutive appointments we have
        let blockSize = 2; // We already have 2 consecutive appointments
        let j = i + 1;

        // Count how many more consecutive appointments follow
        while (
          j < allAppointmentsList.length - 1 &&
          allAppointmentsList[j].end === allAppointmentsList[j + 1].start
        ) {
          blockSize++;
          j++;
        }

        // If we have 2 or more consecutive appointments, it's a conflict
        if (blockSize >= 2) {
          return true;
        }
      }
    }

    // Additional check: if we have 2 consecutive appointments and one of them is the proposed,
    // check if this would create a block that spans 2 or more time slots
    if (allAppointmentsList.length >= 2) {
      for (let i = 0; i < allAppointmentsList.length - 1; i++) {
        const current = allAppointmentsList[i];
        const next = allAppointmentsList[i + 1];

        // If appointments are consecutive and one is the proposed appointment
        if (
          current.end === next.start &&
          (current.isProposed || next.isProposed)
        ) {
          // Calculate the total time span of the consecutive block
          const blockStart = current.start;
          const blockEnd = next.end;
          const totalMinutes = blockEnd - blockStart;

          // If the block spans 2 or more time slots, it's a conflict
          const timeSlotsInBlock = totalMinutes / durationMinutes;
          if (timeSlotsInBlock >= 2) {
            return true;
          }
        }
      }
    }

    return false;
  };

  // Helper function to check if a specific time slot is available
  const isTimeSlotAvailable = (date, hour, minute = 0) => {
    // First check if the time slot is in the past
    if (isTimeSlotInPast(date, hour, minute)) {
      return false;
    }

    if (!allAppointments || allAppointments.length === 0) {
      return true;
    }

    const dateString = date.toISOString().split("T")[0];

    // Check if any appointment conflicts with this time slot
    return !allAppointments.some((appointment) => {
      // Only check appointments that have been scheduled (have fecha, horaInicio, horaFin)
      if (
        !appointment.fecha ||
        !appointment.horaInicio ||
        !appointment.horaFin
      ) {
        return false; // Skip unscheduled appointments
      }

      try {
        // Parse appointment date consistently with local timezone
        const appointmentDate = new Date(appointment.fecha + "T00:00:00");

        // Check if the date is valid
        if (isNaN(appointmentDate.getTime())) {
          return false; // Skip invalid dates
        }

        const appointmentDateString = appointmentDate
          .toISOString()
          .split("T")[0];
        const [appointmentStartHour, appointmentStartMinute] =
          appointment.horaInicio.split(":").map(Number);
        const [appointmentEndHour, appointmentEndMinute] = appointment.horaFin
          .split(":")
          .map(Number);

        // Validate parsed times
        if (
          isNaN(appointmentStartHour) ||
          isNaN(appointmentStartMinute) ||
          isNaN(appointmentEndHour) ||
          isNaN(appointmentEndMinute)
        ) {
          return false;
        }

        // Convert times to minutes for easier comparison
        const appointmentStartMinutes =
          appointmentStartHour * 60 + appointmentStartMinute;
        const appointmentEndMinutes =
          appointmentEndHour * 60 + appointmentEndMinute;
        const slotStartMinutes = hour * 60 + minute;

        // Get appointment duration to calculate slot end time
        let durationMinutes = 60; // Default to 60 minutes (1 hour)
        if (eventData && eventData.duracionCitas) {
          const [durationHours, durationMins] = eventData.duracionCitas
            .split(":")
            .map(Number);
          durationMinutes = durationHours * 60 + durationMins;
        }
        const slotEndMinutes = slotStartMinutes + durationMinutes;

        // Check if dates match and time slots overlap
        return (
          appointmentDateString === dateString &&
          slotStartMinutes < appointmentEndMinutes &&
          slotEndMinutes > appointmentStartMinutes
        );
      } catch (error) {
        logger.error("Error processing appointment:", {
          data: [appointment, error],
        });
        return false; // Skip appointments that cause errors
      }
    });
  };

  // Fetch sender appointments when appointmentData is available
  useEffect(() => {
    const fetchSenderAppointments = async () => {
      if (
        appointmentData?.idUsuarioRemitente &&
        appointmentData?.idEncuentroNegocios
      ) {
        try {
          const senderAppts = await getUserSentAppointmentsForEvent(
            null, // navigate - not needed for this context
            appointmentData.idUsuarioRemitente,
            appointmentData.idEncuentroNegocios,
          );

          // Filter appointments that have horaInicio and horaFin
          const scheduledSenderAppts = senderAppts.filter(
            (appointment) => appointment.horaInicio && appointment.horaFin,
          );

          setSenderAppointments(scheduledSenderAppts);
        } catch (error) {
          logger.error("Error fetching sender appointments:", {
            err: sanitizeError(error),
          });
          setSenderAppointments([]);
        }
      }
    };

    fetchSenderAppointments();
  }, [appointmentData]);

  // Combine all appointments for calendar display
  useEffect(() => {
    logger.info("Appointments passed to modal:", { data: appointments });
    logger.info("Sender appointments:", { data: senderAppointments });
    const combinedAppointments = [...appointments, ...senderAppointments];
    logger.info("Combined appointments:", { data: combinedAppointments });
    setAllAppointments(combinedAppointments);
  }, [appointments, senderAppointments]);

  // Generate date options based on eventData
  useEffect(() => {
    logger.info("eventData: ", { data: eventData });
    if (eventData && eventData.fechaInicioEvento && eventData.fechaFinEvento) {
      // Parse dates and normalize to avoid timezone issues
      const startDate = new Date(eventData.fechaInicioEvento + "T00:00:00");
      const endDate = new Date(eventData.fechaFinEvento + "T00:00:00");

      logger.info("Start date:", { data: [startDate, "End date:", endDate] });
      logger.info("Start date valid:", { data: !isNaN(startDate.getTime()) });
      logger.info("End date valid:", { data: !isNaN(endDate.getTime()) });

      const options = [];

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

      for (
        let dayCursor = new Date(startDate);
        dayCursor.getTime() <= endDate.getTime();
        dayCursor.setDate(dayCursor.getDate() + 1)
      ) {
        const currentDate = new Date(dayCursor);

        // Check if this day is today or in the future
        const isTodayOrFuture = currentDate >= today;

        // Check if this day has available time slots
        const availableTimeSlots = getAvailableTimeSlotsForDate(currentDate);

        // Only add the date if it's today or in the future AND has available time slots
        if (isTodayOrFuture && availableTimeSlots.length > 0) {
          // Format date as "Martes 23, 2025"
          const weekday = currentDate.toLocaleDateString("es-ES", {
            weekday: "long",
          });
          const day = currentDate.getDate();
          const year = currentDate.getFullYear();

          // Capitalize first letter of weekday and format as "Martes 23, 2025"
          const capitalizedWeekday =
            weekday.charAt(0).toUpperCase() + weekday.slice(1);
          const formattedDate = `${capitalizedWeekday} ${day}, ${year}`;

          options.push({
            value: currentDate.toISOString().split("T")[0], // YYYY-MM-DD format
            label: formattedDate,
            date: new Date(currentDate),
          });
        }
      }

      logger.info("options: ", { data: options });

      setDateOptions(options);
    }
  }, [eventData]);

  const handleDateSelect = (date, hour, minute = 0) => {
    setSelectedDate(date);
    setSelectedTime(hour);
    setSelectedMinute(minute);

    // Update the date dropdown selection
    const matchingDateOption = dateOptions.find(
      (option) => option.date.toDateString() === date.toDateString(),
    );
    if (matchingDateOption) {
      setSelectedAppointmentDate(matchingDateOption);

      // Generate time options for the selected date
      const availableTimeSlots = getAvailableTimeSlotsForDate(date);
      setTimeOptions(availableTimeSlots);

      // Update the time dropdown selection if the time slot is available
      const matchingTimeOption = availableTimeSlots.find(
        (option) => option.hour === hour && option.minute === minute,
      );
      if (matchingTimeOption) {
        setSelectedAppointmentTime(matchingTimeOption);
      } else {
        // If no exact match found, try to find a time slot that starts at this hour
        const fallbackTimeOption = availableTimeSlots.find(
          (option) => option.hour === hour,
        );
        if (fallbackTimeOption) {
          setSelectedAppointmentTime(fallbackTimeOption);
        }
      }
    }
  };

  const handleAppointmentDateChange = (selectedOption) => {
    setSelectedAppointmentDate(selectedOption);
    setSelectedAppointmentTime(null); // Reset time selection when date changes
    setSelectedTime(null); // Clear the selected time for calendar display
    setSelectedMinute(null); // Clear the selected minute for calendar display

    if (selectedOption) {
      // Update the calendar to show the selected date
      setSelectedDate(selectedOption.date);

      // Generate time options for the selected date
      const availableTimeSlots = getAvailableTimeSlotsForDate(
        selectedOption.date,
      );
      setTimeOptions(availableTimeSlots);
    } else {
      setTimeOptions([]);
      setSelectedDate(null); // Clear selected date if no option selected
    }
  };

  const handleAppointmentTimeChange = (selectedOption) => {
    setSelectedAppointmentTime(selectedOption);
    if (selectedOption) {
      setSelectedTime(selectedOption.hour);
      setSelectedMinute(selectedOption.minute);

      // Ensure selectedDate is set if we have a selected date
      if (selectedAppointmentDate && selectedAppointmentDate.date) {
        setSelectedDate(selectedAppointmentDate.date);
      }
    } else {
      // Clear time selection if option is cleared
      setSelectedTime(null);
      setSelectedMinute(null);
    }
  };

  const handleAutoAssign = () => {
    // If a date is already selected, just pick the first available time
    if (selectedAppointmentDate && timeOptions.length > 0) {
      const firstAvailableTime = timeOptions[0];
      setSelectedAppointmentTime(firstAvailableTime);
      setSelectedTime(firstAvailableTime.hour);
      setSelectedMinute(firstAvailableTime.minute);
    } else if (dateOptions.length > 0) {
      // If no date is selected, pick the first available date and its first available time
      const firstAvailableDate = dateOptions[0];
      setSelectedAppointmentDate(firstAvailableDate);
      setSelectedDate(firstAvailableDate.date);

      // Generate time options for the selected date
      const availableTimeSlots = getAvailableTimeSlotsForDate(
        firstAvailableDate.date,
      );
      setTimeOptions(availableTimeSlots);

      // Select the first available time
      if (availableTimeSlots.length > 0) {
        const firstAvailableTime = availableTimeSlots[0];
        setSelectedAppointmentTime(firstAvailableTime);
        setSelectedTime(firstAvailableTime.hour);
        setSelectedMinute(firstAvailableTime.minute);
      }
    } else {
      // No dates available for auto-assign
    }
  };

  const handleConfirm = async () => {
    if (!selectedDate || selectedTime === null) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Get the selected time slot details
      const selectedTimeSlot = selectedAppointmentTime;
      const timeString = selectedTimeSlot
        ? selectedTimeSlot.value
        : `${selectedTime.toString().padStart(2, "0")}:00`;
      // Format date as YYYY-MM-DD without timezone conversion
      const year = selectedDate.getFullYear();
      const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
      const day = selectedDate.getDate().toString().padStart(2, "0");
      const fecha = `${year}-${month}-${day}`;

      logger.info("Selected date:", { data: selectedDate });
      logger.info("Formatted fecha:", { data: fecha });

      // Calculate end time based on event duration
      let durationMinutes = 60; // Default to 60 minutes (1 hour)

      if (eventData && eventData.duracionCitas) {
        // Parse event duration (format: "00:20", "01:30", etc.)
        const [hours, minutes] = eventData.duracionCitas.split(":").map(Number);
        durationMinutes = hours * 60 + minutes;
      }

      // Calculate end time using the actual selected time slot
      const [startHour, startMinute] = timeString.split(":").map(Number);
      const startTime = new Date(selectedDate);
      startTime.setHours(startHour, startMinute, 0, 0);
      const endTime = new Date(
        startTime.getTime() + durationMinutes * 60 * 1000,
      );

      logger.info("startTime: ", { data: startTime });
      const appointmentInfo = {
        fecha: fecha, // Format as YYYY-MM-DD without timezone issues
        horaInicio: timeString,
        horaFin: `${endTime.getHours().toString().padStart(2, "0")}:${endTime
          .getMinutes()
          .toString()
          .padStart(2, "0")}`,
        fechaHoraInicioPlanificada: startTime.toISOString(),
      };

      await onConfirm(appointmentInfo);
    } catch (error) {
      logger.error("Error confirming appointment:", {
        err: sanitizeError(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedMinute(null);
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {t("components.appointmentScheduleModal.title")}
            {user?.nombre && (
              <span className={styles.userName}> - {user.nombre}</span>
            )}
          </h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.description}>
            {t("components.appointmentScheduleModal.subtitle")}
          </div>

          <div className={styles.selectsContainer}>
            <div className={styles.dateSelectContainer}>
              <label className={styles.dateSelectLabel}>
                {t("components.appointmentScheduleModal.appointmentDate")}
              </label>
              <div className={styles.dateSelectWrapper}>
                <Select
                  options={dateOptions}
                  isSearchable={false}
                  value={selectedAppointmentDate}
                  onChange={handleAppointmentDateChange}
                  placeholder={t(
                    "components.appointmentScheduleModal.selectDate",
                  )}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderRadius: "8px",
                      width: "220px",
                      height: "44px",
                      fontSize: "14px",
                      cursor: "pointer",
                      textAlign: "left",
                      borderColor: "#909092",
                      backgroundColor: "#FCFCFC",
                    }),
                    container: (baseStyles) => ({
                      ...baseStyles,
                      width: "100%",
                    }),
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: "#909092",
                    }),
                    singleValue: (baseStyles) => ({
                      ...baseStyles,
                      color: "#231f20",
                    }),
                    indicatorSeparator: (baseStyles) => ({
                      ...baseStyles,
                      display: "none",
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 9999,
                    }),
                    menuList: (baseStyles) => ({
                      ...baseStyles,
                      maxHeight: "200px",
                      overflowY: "auto",
                      padding: "4px 0",
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isSelected
                        ? "#3b82f6"
                        : state.isFocused
                          ? "#f3f4f6"
                          : "white",
                      color: state.isSelected ? "white" : "#231f20",
                      cursor: "pointer",
                      padding: "8px 12px",
                      fontSize: "16px",
                      textAlign: "left",
                      "&:hover": {
                        backgroundColor: state.isSelected
                          ? "#3b82f6"
                          : "#f3f4f6",
                      },
                    }),
                  }}
                />
              </div>
            </div>

            <div className={styles.timeSelectContainer}>
              <label className={styles.timeSelectLabel}>
                {t("components.appointmentScheduleModal.appointmentTime")}
              </label>
              <div className={styles.timeSelectWrapper}>
                <div className={styles.selectWrapper}>
                  <Select
                    options={timeOptions}
                    isSearchable={false}
                    value={selectedAppointmentTime}
                    onChange={handleAppointmentTimeChange}
                    placeholder={t(
                      "components.appointmentScheduleModal.selectTime",
                    )}
                    isDisabled={!selectedAppointmentDate}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderRadius: "8px",
                        width: "150px",
                        height: "44px",
                        fontSize: "14px",
                        cursor: "pointer",
                        textAlign: "left",
                        borderColor: "#909092",
                        backgroundColor: "#FCFCFC",
                      }),
                      container: (baseStyles) => ({
                        ...baseStyles,
                        width: "100%",
                      }),
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        color: "#909092",
                      }),
                      singleValue: (baseStyles) => ({
                        ...baseStyles,
                        color: "#231f20",
                      }),
                      indicatorSeparator: (baseStyles) => ({
                        ...baseStyles,
                        display: "none",
                      }),
                      menu: (baseStyles) => ({
                        ...baseStyles,
                        maxHeight: "200px",
                        overflowY: "auto",
                        zIndex: 9999,
                      }),
                      menuList: (baseStyles) => ({
                        ...baseStyles,
                        maxHeight: "200px",
                        overflowY: "auto",
                        padding: "4px 0",
                      }),
                      option: (baseStyles, state) => ({
                        ...baseStyles,
                        backgroundColor: state.isSelected
                          ? "#3b82f6"
                          : state.isFocused
                            ? "#f3f4f6"
                            : "white",
                        color: state.isSelected ? "white" : "#231f20",
                        cursor: "pointer",
                        padding: "8px 12px",
                        fontSize: "16px",
                        textAlign: "left",
                        "&:hover": {
                          backgroundColor: state.isSelected
                            ? "#3b82f6"
                            : "#f3f4f6",
                        },
                      }),
                    }}
                  />
                </div>
                <button
                  className={styles.autoAssignButton}
                  onClick={handleAutoAssign}
                  //   disabled={
                  //     !selectedAppointmentDate || timeOptions.length === 0
                  //   }
                >
                  <AutoAssignIcon className={styles.autoAssignIcon} />
                  {t("components.appointmentScheduleModal.autoAssign")}
                </button>
              </div>
            </div>
          </div>

          <div className={styles.legendContainer}>
            <div className={styles.legendItem}>
              <div
                className={styles.legendCircle}
                style={{ backgroundColor: "#D2DEFA" }}
              />
              <span className={styles.legendText}>
                {t("components.appointmentScheduleModal.scheduledAppointment")}
              </span>
            </div>
            <div className={styles.legendItem}>
              <div
                className={styles.legendCircle}
                style={{ backgroundColor: "#1D5AE8" }}
              />
              <span className={styles.legendText}>
                {t("components.appointmentScheduleModal.confirmedAppointment")}
              </span>
            </div>
            <div className={styles.legendItem}>
              <div
                className={styles.legendCircle}
                style={{ backgroundColor: "#D4F5FF" }}
              />
              <span className={styles.legendText}>
                {t(
                  "components.appointmentScheduleModal.virtualScheduledAppointment",
                )}
              </span>
            </div>
            <div className={styles.legendItem}>
              <div
                className={styles.legendCircle}
                style={{ backgroundColor: "#00AEED" }}
              />
              <span className={styles.legendText}>
                {t(
                  "components.appointmentScheduleModal.virtualConfirmedAppointment",
                )}
              </span>
            </div>
            <div className={styles.legendItem}>
              <div
                className={`${styles.legendCircle} ${styles.legendCircleLunchBreak}`}
              />
              <span className={styles.legendText}>
                {t("components.appointmentScheduleModal.lunchBreak")}
              </span>
            </div>
          </div>

          <div className={styles.calendarContainer}>
            <CustomCalendar
              appointments={allAppointments}
              onDateSelect={handleDateSelect}
              eventData={eventData}
              participatingCompanyData={participatingCompanyData}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              selectedMinute={selectedMinute}
              senderAppointments={senderAppointments}
              currentAppointment={appointmentData}
              allowDragAllAppointments={false}
              disableDragAndDrop={true}
            />
          </div>
        </div>

        <div className={styles.footer}>
          <button
            className={styles.confirmButton}
            onClick={handleConfirm}
            // disabled={!selectedDate || selectedTime === null || isSubmitting}
          >
            {isSubmitting
              ? t("common.saving")
              : t("components.appointmentScheduleModal.scheduleAppointment")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentScheduleModal;
